import React, { useEffect, useState } from 'react';
import { Popover } from 'antd';

import { useIsProduction } from '../../../hooks/useIsProduction';
import {
  buildRiskFactors,
  LaneScoreMode,
  scoreFactorColor,
} from './quoteScores';
import styles from './styles.module.css';

const HIGH_RISK_PERCENT = 70;

interface RiskMetricProps {
  percent: number;
  mode: LaneScoreMode;
}

/**
 * The card's Risk %, with its factor breakdown in a popover. Mock score (see
 * quoteScores.ts), so production shows "--%" until there is a real source.
 */
const RiskMetric: React.FC<RiskMetricProps> = ({ percent, mode }) => {
  const [open, setOpen] = useState(false);
  const isProduction = useIsProduction();

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [open]);

  if (isProduction) {
    return (
      <div className={styles.rateMetric}>
        <div
          className={styles.rateMetricValueNone}
          aria-label='Risk not available'
        >
          --%
        </div>
        <div className={styles.rateMetricLabel}>Risk</div>
      </div>
    );
  }

  const highClass =
    percent >= HIGH_RISK_PERCENT ? styles.rateMetricValueHigh : '';

  const popover = (
    <div
      className={styles.scorePopover}
      role='dialog'
      aria-label='Risk score'
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.scorePopoverHeader}>
        <span className={styles.scorePopoverTitle}>Risk score</span>
        <span className={`${styles.scorePopoverTotal} ${highClass}`}>
          {percent}%
        </span>
      </div>
      <div className={styles.scorePopoverFactors}>
        {buildRiskFactors(percent, mode).map((factor) => (
          <div key={factor.label} className={styles.scorePopoverRowRisk}>
            <span className={styles.scorePopoverFactorLabel}>
              {factor.label}
            </span>
            <span
              className={styles.scorePopoverFactorValue}
              // eslint-disable-next-line react/forbid-dom-props -- a colour computed from the score
              style={{ color: scoreFactorColor(factor.valuePercent) }}
            >
              {factor.valuePercent}%
            </span>
          </div>
        ))}
      </div>
      <div className={styles.scorePopoverNote}>
        Illustrative for now: not yet based on live data.
      </div>
    </div>
  );

  return (
    <div
      className={styles.rateMetric}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
      }}
    >
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger='click'
        placement='bottomRight'
        arrow={false}
        destroyOnHidden
        overlayInnerStyle={{
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
        }}
        content={popover}
      >
        <button
          type='button'
          className={`${styles.rateMetricValueBtn} ${highClass}`}
          aria-expanded={open}
          aria-label={`Risk ${percent}%`}
        >
          {percent}%
        </button>
      </Popover>
      <div className={styles.rateMetricLabel}>Risk</div>
    </div>
  );
};

export default RiskMetric;
